import moment from "moment";
import download from "downloadjs";
import { unparse as convertToCSV } from "papaparse/papaparse.min";
import { downloadCsv, validateAndNormalizeDocument, validateTypeUser } from "../../utils";
import { SERVER_URL } from "../../config";
import parseCsv from "../../utils/parseCsv";
import validators from "../../utils/validators";

export const getPanelValidationErrorMessage = ({ invalidDocumentsCount = 0, invalidTypesCount = 0 } = {}) => {
  const messageParts = [];

  if (invalidDocumentsCount > 0) {
    messageParts.push(
      <strong key="invalid-documents">
        {`${invalidDocumentsCount} documento${invalidDocumentsCount > 1 ? "s" : ""}`}
      </strong>
    );
  }

  if (invalidTypesCount > 0) {
    messageParts.push(
      <strong key="invalid-types">
        {`${invalidTypesCount} tipo${invalidTypesCount > 1 ? "s" : ""} de médico${invalidTypesCount > 1 ? "s" : ""}`}
      </strong>
    );
  }

  if (messageParts.length === 0) {
    return "Baixe a planilha detalhada para visualizar e corrigir.";
  }

  return (
    <>
      Identificamos {messageParts[0]}
      {messageParts.length > 1 ? <> e {messageParts[1]}</> : null} fora do padrão. Baixe a planilha detalhada para visualizar e corrigir.
    </>
  );
};
 
export const exporter = (data) => {
  const dataForExport = data.map((item) => item.user.map(user => (
    {
      'Nome': user.name,
      'Cpf': user.cpf,
      'Especialidade': user?.specialtiesUsers[0]?.specialties?.name,
      'Rua': user.address?.address,
      'Numero': user.address?.number,
      'Complemento': user.address?.complement,
      'Bairro': user.address?.district,
      'Cep': user.address?.cep,
      'Cidade': user.address?.city,
      'Estado': user.address?.state,
      'Telefone': user.phone,
      'Email': user.email,
      'Gênero': user.genderUser?.name,
      'Fonte': user.address?.sourceAddress?.name,
      'Status de entrega': user.address?.statusDeliveryAddressId === 0 ? 'Confirmada' : user.address?.statusDeliveryAddressId === 1 ? 'Devolvida' : 'Não',
      'Data de criação do endereço': moment(user.address?.createdAt).format("DD/MM/YYYY"),
      "Tipo de Endereço": user.address?.typeUserId || "",
      "Outro Tipo de Endereço": user.address?.otherAddressType || "",
    })));
 
  const csv = convertToCSV(
    {
      data: dataForExport.flat(),
      fields: [
        "Nome",
        "Cpf",
        'Especialidade',
        "Rua",
        "Numero",
        "Complemento",
        "Bairro",
        "Cep",
        "Cidade",
        "Estado",
        "Telefone",
        "Email",
        "Gênero",
        'Fonte',
        'Status de entrega',
        "Data de criação do endereço",
        "Tipo de Endereço",
        "Outro Tipo de Endereço",
      ],
    },
    { delimiter: ";" }
  );
 
  downloadCsv(csv, "panels");
};
 
export const exporterSingle = (data) => {
  const dataForExport = data.user.map((user) => (
    {
      'tipo': user.typeUserId,
      'documento': user.document,
      'nome': user.name,
      'cpf': user.cpf,
      'especialidade': user?.specialtiesUsers?.map(specialtiesUsers => specialtiesUsers.specialties?.name),
      'rua': user.address?.address,
      'numero': user.address?.number,
      'complemento': user.address?.complement,
      'bairro': user.address?.district,
      'cep': user.address?.cep,
      'cidade': user.address?.city,
      'uf': user.address?.state,
      'telefone': user.phone,
      'email': user.email,
      'sexo': user.genderUser?.name,
      "tipo de endereco": user.address?.typeAddressId || "",
      "outro tipo de endereco": user.address?.otherAddressType || "",
      //'id usuario': user.id,
      //'id painel': data.id,
    }));

  const csv = convertToCSV(
    {
      data: dataForExport.flat(),
      fields: [
        'tipo',
        'documento',
        'nome',
        'cpf',
        'especialidade',
        'rua',
        'numero',
        'complemento',
        'bairro',
        'cep',
        'cidade',
        'uf',
        'telefone',
        'email',
        'sexo',
        "tipo de endereco",
        "outro tipo de endereco",
        //'id usuario',
        //'id painel',
        //'tipo edição',
      ],
    },
    { delimiter: ";" }
  );
 
  downloadCsv(csv, "panels");
};

/**
 * Download do CSV de validação do painel armazenado no S3
 * @param {Object} params - Parâmetros da função
 * @param {string} params.panelId - ID do painel
 * @param {string} params.panelName - Nome do painel (opcional, para nome do arquivo)
 * @returns {Promise<void>}
 */
export const downloadValidationCsv = async ({ panelId, panelName }) => {
  const response = await fetch(`${SERVER_URL}/panels/${encodeURIComponent(panelId)}/csv-output`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erro ao baixar CSV' }));
    throw new Error(error.message || 'Erro ao baixar CSV de validação');
  }

  const blob = await response.blob();
  
  const safePanelName = panelName
    ? panelName
        .trim()
        // eslint-disable-next-line no-control-regex
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
        .replace(/\s+/g, '_')
    : '';
  
  const fileName = safePanelName
    ? `${panelId}_${safePanelName}.csv`
    : `${panelId}.csv`;
  
  download(blob, fileName);
};
 
/**
 * Valida e normaliza o CSV do painel
 * @param {Array} rows - Array de objetos com as linhas do CSV
 * @returns {Object} - Objeto com os resultados da validação
 * @returns {boolean} returns.valid - Indica se o CSV é válido
 * @returns {Array} returns.errors - Array de objetos com os erros da validação
 * @returns {Array} returns.normalizedRows - Array de objetos com os dados normalizados
 */
export const validatePanelCsv = async (csvText, originalHeaders) => {
  try {
    const rows = await parseCsv(csvText, "panels").catch((e) => {
      throw new Error("Erro ao processar CSV: " + e.message);
    });

    if (!rows || !rows.length) {
      throw new Error("CSV vazio ou inválido");
    }

    const columns = Object.keys(rows[0]);
    const headerCheck = validators.validateCsvHeaders(columns, "panels");

    const missingColumns = [];
    if (headerCheck && headerCheck.errors) {
      headerCheck.errors.forEach((err) => missingColumns.push(err.column));
    }

    let invalidDocumentsCount = 0;
    let invalidTypesCount = 0;

    const validationResults = rows.map((row, index) => {
      const line = index + 2;
      const errors = [];

      const doc = validateAndNormalizeDocument(row.documento);
      if (!doc.isValid) {
        invalidDocumentsCount += 1;
        errors.push(doc.error || "Documento inválido");
      }

      if (!validateTypeUser(row.tipo)) {
        invalidTypesCount += 1;
        errors.push(`Tipo de usuário inválido: ${row.tipo}`);
      }

      return {
        rowNumber: line,
        isValid: errors.length === 0,
        errors,
      };
    });

    const hasErrors = validationResults.some((r) => !r.isValid);
    const totalErrors = validationResults.reduce((sum, r) => sum + r.errors.length, 0);

    const normalizedRows = rows.map((row) => {
      const doc = validateAndNormalizeDocument(row.documento);
      return { ...row, documento: doc.normalizedDocument };
    });

    return {
      isValid: !hasErrors && missingColumns.length === 0,
      rows: normalizedRows,
      validationResults,
      missingColumns,
      totalErrors,
      invalidDocumentsCount,
      invalidTypesCount,
      originalHeaders,
    };
  } catch (error) {
    return {
      isValid: false,
      error: error.message,
      rows: [],
      validationResults: [],
      missingColumns: [],
      totalErrors: 0,
      invalidDocumentsCount: 0,
      invalidTypesCount: 0,
      originalHeaders,
    };
  }
};
 
 
